import './style.css';
import type { ItemEvent, MenuResult, OnlineMatchInfo, RaceConfig, RaceResult } from './core/types';
import { IDLE_INPUT } from './core/types';
import { CHARACTERS } from './data/roster';
import { TRACKS } from './data/tracks';
import { Track } from './game/track';
import { World } from './game/world';
import { GameRenderer } from './render/renderer';
import { buildMinimap, buildTrackLayer, type TrackLayer } from './render/trackRenderer';
import { InputManager, isTextEntryTarget } from './core/input';
import { devices } from './core/control-config';
import { createLoop, type Loop } from './core/loop';
import { AudioEngine } from './core/audio';
import { createMenu, showResults } from './ui/menu';
import { showSplash } from './ui/splash';
import { clearQuip, showQuip } from './ui/quips';
import { createTouchControls, type TouchControls } from './ui/touch';
import { isMobileDevice, isTouchDevice } from './core/device';
import { quipFor, type QuipKind } from './data/quips';
import { showBattleResult, showPause, showStartHint, type ControlPlayer, type Overlay } from './ui/controls';
import { OnlineRaceSync } from './net/race';
import { openOnlineLobby, type OnlineMatch } from './net/lobby';

const canvasEl = document.querySelector<HTMLCanvasElement>('#game');
const uiEl = document.querySelector<HTMLElement>('#ui');
if (!canvasEl || !uiEl) throw new Error('Hiányzó #game vagy #ui elem.');
const canvas: HTMLCanvasElement = canvasEl;
const uiRoot: HTMLElement = uiEl;

// ?debug=1 a produkciós buildben is kiadja a belső állapotot (tesztekhez)
const DEBUG = import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug');

const input = new InputManager(window);
const audio = new AudioEngine();

if (DEBUG) {
  (window as unknown as { __audio?: AudioEngine }).__audio = audio;
}

interface BuiltTrack {
  track: Track;
  layer: TrackLayer;
  minimap: HTMLCanvasElement;
}

const trackCache = new Map<string, BuiltTrack>();

const getTrack = (id: string): BuiltTrack => {
  const cached = trackCache.get(id);
  if (cached) return cached;
  const def = TRACKS.find((t) => t.id === id) ?? TRACKS[0];
  const t0 = performance.now();
  const track = new Track(def);
  const t1 = performance.now();
  const layer = buildTrackLayer(track);
  const t2 = performance.now();
  const minimap = buildMinimap(track, 200);
  const t3 = performance.now();
  if (DEBUG) {
    console.info(
      `[track] ${def.id} · geometria ${(t1 - t0).toFixed(0)} ms · réteg ${(t2 - t1).toFixed(0)} ms · minimap ${(t3 - t2).toFixed(0)} ms`,
    );
  }
  const built = { track, layer, minimap };
  trackCache.set(id, built);
  return built;
};

const announce = (text: string): HTMLElement => {
  const div = document.createElement('div');
  div.className = 'loading';
  div.textContent = text;
  uiRoot.appendChild(div);
  return div;
};

const toast = (text: string, duration = 4200): HTMLElement => {
  const div = document.createElement('div');
  div.className = 'toast';
  div.textContent = text;
  uiRoot.appendChild(div);
  window.setTimeout(() => {
    div.classList.add('is-out');
    window.setTimeout(() => div.remove(), 350);
  }, duration);
  return div;
};

const startRace = (choice: MenuResult, onlineMatch?: OnlineMatch): { world: World; renderer: GameRenderer } => {
  const match = onlineMatch?.match;
  const def = TRACKS.find((t) => t.id === (match?.trackId ?? choice.trackId)) ?? TRACKS[0];
  const { track, layer, minimap } = getTrack(def.id);
  const params = new URLSearchParams(window.location.search);
  const lapsParam = Number(params.get('laps'));
  const config: RaceConfig = match
    ? {
        mode: match.humans.length >= 4 ? 4 : match.humans.length === 2 ? 2 : 1,
        laps: match.laps,
        trackId: track.def.id,
        picks: match.humans.map((human) => human.characterId),
        seed: match.seed,
        difficulty: 0.62,
        night: match.night,
        noAi: !match.ai,
        online: true,
      }
    : {
        mode: choice.mode,
        laps: Number.isFinite(lapsParam) && lapsParam > 0 ? Math.min(9, Math.round(lapsParam)) : track.def.laps,
        trackId: track.def.id,
        picks: choice.picks,
        seed: (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0,
        difficulty: 0.62,
        autopilot: params.get('autopilot') === '1',
        practice: choice.practice === true || params.get('practice') === '1',
        battle: choice.battle === true || params.get('battle') === '1',
        night: choice.night === true || params.get('night') === '1',
        noAi: choice.noAi === true || params.get('noai') === '1',
      };
  const world = new World({ track, config });

  // online: névcímke a kocsik fölé, és egy kliens = egy teljes képernyős nézet
  let views: (() => World['players']) | undefined;
  if (match) {
    match.humans.forEach((human, index) => {
      const kart = world.players[index];
      if (kart) kart.tag = human.name;
    });
    const localIndex = Math.max(0, match.humans.findIndex((human) => human.id === match.you.id));
    views = () => {
      const kart = world.players[localIndex];
      return kart ? [kart] : [];
    };
  }

  audio.setMusic('race');
  const renderer = new GameRenderer(canvas, world, layer, minimap, views);
  if (DEBUG) {
    (window as unknown as {
      __csf?: {
        world: World;
        renderer: GameRenderer;
        input: InputManager;
        devices: typeof devices;
        audio: AudioEngine;
        online?: OnlineMatch;
      };
    }).__csf = { world, renderer, input, devices, audio, online: onlineMatch };
  }
  return { world, renderer };
};

type RaceOutcome = 'finished' | 'restart' | 'menu' | 'lobby';

const race = (world: World, renderer: GameRenderer, onlineMatch?: OnlineMatch): Promise<RaceOutcome> =>
  new Promise((resolve) => {
    let lastCountdown: number | null = null;
    let finishing = false;
    let paused = false;
    let done = false;
    let pauseOverlay: Overlay | null = null;
    let pauseNotice: HTMLElement | null = null;
    let touch: TouchControls | null = null;
    let hint: Overlay | null = null;
    let loop: Loop;
    let finishTimer = 0;
    let quipAt = -99;
    let startQuipped = false;
    let lastLapQuipped = false;
    let finishQuipped = false;

    const maybeQuip = (kart: World['players'][number], kind: QuipKind): void => {
      if (done || paused) return;
      if (kind !== 'finish' && world.time - quipAt < 6) return;
      quipAt = world.time;
      showQuip(uiRoot, kart.character, quipFor(kart.character.id, kind));
    };

    const controlPlayers = (): ControlPlayer[] =>
      world.players.map((k) => ({
        playerIndex: k.playerIndex,
        characterName: k.character.short,
      }));

    /** Online: mindenki a saját gépén a 0-s eszközt használja, nem a slotjáét. */
    const localControls = (): ControlPlayer[] => {
      const kart = localKart();
      return [{ playerIndex: 0, characterName: kart?.character.short ?? '' }];
    };

    const cleanup = (): void => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onRaceKey, true);
      window.clearTimeout(finishTimer);
      // a hálózati események ne szóljanak bele a következő futamba
      sync?.destroy();
      // a motorhang ne ragadjon bent a menüben / eredményképernyőn
      audio.stopEngines();
      audio.setMusic('menu');
      clearQuip();
      input.setTouchState(null);
      touch?.destroy();
      touch = null;
      hint?.close();
      hint = null;
      pauseOverlay?.close();
      pauseOverlay = null;
      pauseNotice?.remove();
      pauseNotice = null;
      if (DEBUG) delete (window as unknown as { __loop?: Loop }).__loop;
    };

    const finish = (outcome: RaceOutcome): void => {
      if (done) return;
      done = true;
      loop.stop();
      cleanup();
      resolve(outcome);
    };

    // a gazda fül háttérbe került: a vendégek ne prediktáljanak tovább
    const setHostPause = (paused: boolean): void => {
      if (paused) {
        if (pauseNotice) return;
        pauseNotice = toast('A gazda ablaka háttérben van – a verseny szünetel.', 60_000);
        return;
      }
      pauseNotice?.remove();
      pauseNotice = null;
      toast('Folytatás…');
    };

    // ---- online szinkron ----
    const sync: OnlineRaceSync | null = onlineMatch
      ? new OnlineRaceSync(onlineMatch.client, onlineMatch.match, {
          onEvents: (events) => {
            for (const e of events) {
              handleItemEvent(e, renderer);
              if (e.kind === 'hit' && e.targetId !== undefined) {
                const target = world.karts.find((k) => k.id === e.targetId);
                if (target?.isPlayer) maybeQuip(target, 'hit');
              }
            }
          },
          onBumps: (bumps) => {
            for (const b of bumps) {
              audio.sfx('bump');
              renderer.fx.burst(b.at.x, b.at.y, '#ffe066', Math.round(3 + b.strength * 6), 80);
            }
          },
          onResult: (result) => {
            world.result = result;
            world.phase = 'finished';
          },
          onLeft: (slot, name) => {
            const index = onlineMatch.match.humans.findIndex((human) => human.slot === slot);
            const kart = world.players[index];
            if (kart) world.takeOver(kart.id);
            toast(`${name} kilépett – a kocsiját AI viszi tovább.`);
          },
          onClosed: (message) => {
            if (done) return;
            toast(message);
            finish('menu');
          },
          onResume: (seconds) => {
            if (onlineMatch.match.you.host) return;
            if (seconds < 1) return;
            toast('Visszatértél – a kocsi a szerver pozícióján folytatja.');
          },
          onHostPause: setHostPause,
        })
      : null;

    const localKart = (): World['players'][number] | undefined =>
      world.players[sync ? sync.localIndex : 0];

    /** A szimuláció eseményeinek kiosztása (hang + FX + beszólás). */
    const collectWorld = (): { events: ItemEvent[]; bumps: { at: { x: number; y: number }; strength: number }[] } => {
      const events = world.drainEvents();
      for (const e of events) {
        handleItemEvent(e, renderer);
        if (e.kind === 'hit' && e.targetId !== undefined) {
          const target = world.karts.find((k) => k.id === e.targetId);
          if (target?.isPlayer) maybeQuip(target, 'hit');
        }
      }
      const bumps = world.drainBumps();
      for (const b of bumps) {
        audio.sfx('bump');
        renderer.fx.burst(b.at.x, b.at.y, '#ffe066', Math.round(3 + b.strength * 6), 80);
      }
      return { events, bumps };
    };

    const openPause = (): void => {
      if (paused || done || world.phase === 'finished') return;
      paused = true;
      audio.stopEngines();
      touch?.hide();
      hint?.close();
      hint = null;
      pauseOverlay = showPause(uiRoot, {
        players: sync ? localControls() : controlPlayers(),
        musicOn: audio.musicEnabled,
        onToggleMusic: () => audio.toggleMusic(),
        sfxOn: audio.sfxEnabled,
        onToggleSfx: () => audio.toggleSfx(),
        predictionOn: sync && !sync.host ? sync.predictionOn : undefined,
        onTogglePrediction: sync && !sync.host ? () => sync.togglePrediction() : undefined,
        online: sync !== null,
        onResume: () => {
          paused = false;
          pauseOverlay = null;
          touch?.show();
        },
        onRestart: () => finish('restart'),
        onMenu: () => finish('menu'),
      });
    };

    const onRaceKey = (e: KeyboardEvent): void => {
      if (e.code !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (paused) {
        pauseOverlay?.close();
        pauseOverlay = null;
        paused = false;
      } else {
        openPause();
      }
    };

    loop = createLoop({
      update(dt) {
        input.poll();

        if (sync) {
          // online: a szünetpanel nem állítja meg a világot, csak a bemenetet nullázza
          const drive = paused ? IDLE_INPUT : input.state(0);
          if (sync.host) {
            sync.hostApplyInputs(world, drive);
            world.step(dt);
            const { events, bumps } = collectWorld();
            sync.hostAfterStep(world, events, bumps);
            if (world.phase === 'finished' && world.result) sync.hostFinish(world.result);
          } else {
            sync.guestUpdate(world, drive, dt);
          }
        } else {
          if (paused) return;
          for (let i = 0; i < world.players.length; i++) world.setPlayerInput(i, input.state(i));
          world.step(dt);
          collectWorld();
        }

        renderer.fx.update(dt, world.karts);
        if (touch) touch.setSpeed(localKart()?.speedKmh ?? 0);

        if (!startQuipped && world.phase === 'racing' && world.time > 0.4) {
          startQuipped = true;
          const first = localKart();
          if (first) maybeQuip(first, 'start');
        }
        if (!lastLapQuipped && world.phase === 'racing') {
          const lastLap = world.players.find((k) => k.lap === world.config.laps && k.progress > 0.04);
          if (lastLap) {
            lastLapQuipped = true;
            maybeQuip(lastLap, 'lastlap');
          }
        }

        const cd = world.hudCountdown();
        if (cd !== null && cd !== lastCountdown) {
          lastCountdown = cd;
          audio.countdown(cd);
        }

        const audible = sync
          ? world.players.slice(sync.localIndex, sync.localIndex + 1)
          : world.players;
        audio.updateEngines(
          audible.map((k) => ({
            speed: Math.hypot(k.vel.x, k.vel.y),
            maxSpeed: k.stats.topSpeed,
            throttle: k.input.throttle,
            active: !k.finished,
          })),
        );

        const finished = world.phase === 'finished' && (!sync || sync.host || world.result !== null);
        if (finished && !finishing) {
          finishing = true;
          if (!finishQuipped) {
            finishQuipped = true;
            const first = localKart();
            if (first) maybeQuip(first, 'finish');
          }
          audio.sfx('finish');
          audio.stopEngines();
          finishTimer = window.setTimeout(() => finish('finished'), 1100);
        }
      },
      render() {
        renderer.render();
      },
    });
    if (DEBUG) (window as unknown as { __loop?: Loop }).__loop = loop;

    const onResize = (): void => renderer.resize();
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onRaceKey, true);

    if (!world.config.autopilot) {
      const hintPlayers = sync ? localControls() : controlPlayers();
      hint = showStartHint(uiRoot, hintPlayers, isTouchDevice() ? 5000 : 7000, isTouchDevice());
    }
    if (isTouchDevice()) {
      touch = createTouchControls(uiRoot, { onPause: () => openPause() });
      input.setTouchState(touch.state);
      if (DEBUG) {
        (window as unknown as { __touch?: TouchControls }).__touch = touch;
      }
    }
    loop.start();
  });

const handleItemEvent = (e: ItemEvent, renderer: GameRenderer): void => {
  switch (e.kind) {
    case 'pickup':
      audio.sfx('pickup');
      renderer.fx.burst(e.at.x, e.at.y, '#ffc857', 10, 110);
      break;
    case 'use':
      audio.sfx('use');
      break;
    case 'hit':
      audio.sfx('hit');
      renderer.fx.burst(e.at.x, e.at.y, '#ff5c4d', 18, 190);
      break;
    case 'blocked':
      audio.sfx('block');
      renderer.fx.burst(e.at.x, e.at.y, '#3ec6c0', 12, 150);
      break;
    case 'explode':
      audio.sfx('hit');
      renderer.fx.burst(e.at.x, e.at.y, '#8fd14f', 12, 140);
      break;
    default:
      break;
  }
};

const boot = async (): Promise<void> => {
  const unlock = (): void => {
    void audio.start();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
  window.addEventListener('keydown', (e) => {
    if (e.code === 'KeyM' && !isTextEntryTarget(e.target)) audio.toggleMute();
  });

  let choice: MenuResult | null = null;
  let onlineMatch: OnlineMatch | null = null;
  let splashDone = false;
  const params = new URLSearchParams(window.location.search);

  const matchChoice = (match: OnlineMatchInfo): MenuResult => ({
    mode: match.humans.length >= 4 ? 4 : match.humans.length === 2 ? 2 : 1,
    picks: match.humans.map((human) => human.characterId),
    trackId: match.trackId,
    night: match.night,
    noAi: !match.ai,
    online: match,
  });

  const autoMode = Number(params.get('mode') ?? 0);
  if (autoMode === 1 || autoMode === 2 || autoMode === 4) {
    const fallback = CHARACTERS.map((c) => c.id);
    const picks = (params.get('picks') ?? fallback.join(','))
      .split(',')
      .map((s) => s.trim())
      .filter((id) => CHARACTERS.some((c) => c.id === id))
      .slice(0, autoMode);
    while (picks.length < autoMode) picks.push(fallback[picks.length]);
    choice = {
      mode: autoMode as 1 | 2 | 4,
      picks,
      trackId: params.get('track') ?? TRACKS[0].id,
    };
  }

  for (;;) {
    if (!choice && !splashDone) {
      splashDone = true;
      await showSplash(uiRoot);
      void audio.start();
      audio.setMusic('menu');
    }
    if (!choice) {
      const menu = createMenu(uiRoot, {
        characters: CHARACTERS,
        tracks: TRACKS,
        mobile: isMobileDevice(),
        online: {
          open: async () => {
            const opened = await openOnlineLobby(uiRoot, {
              characters: CHARACTERS,
              tracks: TRACKS,
              initialCode: new URLSearchParams(window.location.search).get('join'),
            });
            if (!opened) return null;
            onlineMatch = opened;
            return opened.match;
          },
        },
        sound: {
          get musicEnabled() {
            return audio.musicEnabled;
          },
          get sfxEnabled() {
            return audio.sfxEnabled;
          },
          get muted() {
            return audio.muted;
          },
          toggleMusic: () => audio.toggleMusic(),
          toggleSfx: () => audio.toggleSfx(),
          toggleMute: () => {
            audio.toggleMute();
            return audio.muted;
          },
        },
      });
      choice = await menu.show();
      menu.hide();
      void audio.start();
    }

    const loading = announce('PÁLYA ÉPÍTÉSE…');
    await new Promise((r) => setTimeout(r, 30));
    const current: MenuResult = choice;
    const activeOnline = onlineMatch;
    const { world, renderer } = startRace(current, activeOnline ?? undefined);
    loading.remove();

    const outcome = await race(world, renderer, activeOnline ?? undefined);

    if (outcome === 'menu') {
      activeOnline?.client.close();
      onlineMatch = null;
      choice = null;
      continue;
    }
    if (outcome === 'restart') {
      continue;
    }

    const result: RaceResult = world.result ?? { trackName: world.track.def.name, mode: current.mode, entries: [] };

    // online: ne a menübe, hanem a szobába térjünk vissza
    if (activeOnline) {
      const after = await new Promise<'lobby' | 'menu'>((resolve) => {
        let cleanup = (): void => undefined;
        const off = activeOnline.client.on('closed', (msg) => {
          off();
          cleanup();
          toast(msg.message || 'A szoba bezárult.');
          resolve('menu');
        });
        cleanup = showResults(uiRoot, result, {
          restartLabel: 'VISSZA A SZOBÁBA',
          menuLabel: 'KILÉPÉS',
          hint: 'Enter = vissza a szobába · Esc = kilépés',
          onRestart: () => {
            off();
            cleanup();
            resolve('lobby');
          },
          onMenu: () => {
            off();
            cleanup();
            resolve('menu');
          },
        });
      });
      if (after === 'menu') {
        activeOnline.client.leave();
        onlineMatch = null;
        choice = null;
        continue;
      }
      if (activeOnline.match.you.host) activeOnline.client.toLobby();
      const reopened = await openOnlineLobby(uiRoot, {
        characters: CHARACTERS,
        tracks: TRACKS,
        client: activeOnline.client,
      });
      if (!reopened) {
        activeOnline.client.close();
        onlineMatch = null;
        choice = null;
        continue;
      }
      onlineMatch = reopened;
      choice = matchChoice(reopened.match);
      continue;
    }

    const after: 'restart' | 'menu' = await new Promise((resolve) => {
      if (world.config.battle && result.battle) {
        const overlay = showBattleResult(uiRoot, result, {
          onRestart: () => {
            overlay.close();
            resolve('restart');
          },
          onMenu: () => {
            overlay.close();
            resolve('menu');
          },
        });
        return;
      }
      const cleanup = showResults(uiRoot, result, {
        onRestart: () => {
          cleanup();
          resolve('restart');
        },
        onMenu: () => {
          cleanup();
          resolve('menu');
        },
      });
    });

    if (after === 'menu') choice = null;
  }
};

void boot();
